import { useEffect, useRef, useState } from "react";
import { motion, useInView, useMotionValue, useSpring, animate } from "framer-motion";

export const EASE = [0.16, 1, 0.3, 1];

export const useCoarsePointer = () => {
  const [coarse, setCoarse] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(pointer: coarse)");
    setCoarse(mq.matches);
    const fn = (e) => setCoarse(e.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);
  return coarse;
};

export const useReveal = (threshold = 0.35) => {
  const ref = useRef(null);
  const [show, setShow] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setShow(true);
          obs.disconnect();
        }
      },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, show];
};

export const MaskedLine = ({ children, delay = 0, className = "", innerClassName = "" }) => {
  const [ref, show] = useReveal(0.35);
  return (
    <span ref={ref} className={`block overflow-hidden ${className}`}>
      <motion.span
        className={`inline-block will-change-transform ${innerClassName}`}
        initial={false}
        animate={show ? { y: "0%" } : { y: "115%" }}
        transition={{ duration: 1.05, ease: EASE, delay }}
      >
        {children}
      </motion.span>
    </span>
  );
};

export const SectionTag = ({ index, label, testid }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.6 }}
    transition={{ duration: 0.9, ease: EASE }}
    className="flex items-center gap-4"
    data-testid={testid}
  >
    <span className="font-mono text-xs tracking-[0.3em] text-gold/80">{index}</span>
    <span className="h-px w-12 bg-gradient-to-r from-gold/60 to-transparent" />
    <span className="font-mono text-xs uppercase tracking-[0.3em] text-neutral-400">{label}</span>
  </motion.div>
);

export const MagneticButton = ({ children, href = "#", variant = "gold", className = "", testid }) => {
  const ref = useRef(null);
  const coarse = useCoarsePointer();
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 220, damping: 16, mass: 0.4 });
  const sy = useSpring(y, { stiffness: 220, damping: 16, mass: 0.4 });

  const onMove = (e) => {
    if (coarse || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    x.set((e.clientX - (r.left + r.width / 2)) * 0.35);
    y.set((e.clientY - (r.top + r.height / 2)) * 0.35);
  };
  const onLeave = () => {
    x.set(0);
    y.set(0);
  };

  const base =
    variant === "gold"
      ? "bg-gradient-to-br from-goldlight via-gold to-golddark text-black shadow-[0_8px_30px_rgb(var(--gold)_/_0.25)] hover:shadow-[0_8px_40px_rgb(var(--gold)_/_0.45)]"
      : "border border-gold/30 bg-white/[0.02] text-neutral-200 backdrop-blur-md hover:border-gold/70 hover:text-goldlight";

  return (
    <motion.a
      ref={ref}
      href={href}
      data-testid={testid}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ x: sx, y: sy }}
      whileTap={{ scale: 0.96 }}
      className={`relative inline-flex items-center gap-3 rounded-full px-8 py-4 text-sm font-semibold tracking-wide transition-shadow duration-300 ${base} ${className}`}
    >
      {children}
    </motion.a>
  );
};

export const TiltCard = ({ children, className = "", intensity = 10, testid }) => {
  const ref = useRef(null);
  const coarse = useCoarsePointer();
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 150, damping: 18 });
  const sry = useSpring(ry, { stiffness: 150, damping: 18 });

  const onMove = (e) => {
    if (coarse || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    ry.set(px * intensity);
    rx.set(-py * intensity);
  };
  const onLeave = () => {
    rx.set(0);
    ry.set(0);
  };

  return (
    <div style={{ perspective: 1000 }} className={className}>
      <motion.div
        ref={ref}
        data-testid={testid}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        style={{ rotateX: srx, rotateY: sry, transformStyle: "preserve-3d" }}
        className="h-full"
      >
        {children}
      </motion.div>
    </div>
  );
};

export const Counter = ({ to, decimals = 0, prefix = "", suffix = "", duration = 2.4, className = "", testid }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });
  const [display, setDisplay] = useState("0");
  const prevRef = useRef(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(prevRef.current, to, {
      duration,
      ease: EASE,
      onUpdate: (v) =>
        setDisplay(decimals > 0 ? v.toFixed(decimals) : Math.round(v).toLocaleString("en-IN")),
      onComplete: () => {
        prevRef.current = to;
      },
    });
    return () => controls.stop();
  }, [inView, to, decimals, duration]);

  return (
    <span ref={ref} data-testid={testid} className={className}>
      {prefix}
      {display}
      <span className="text-gold-grad">{suffix}</span>
    </span>
  );
};
